import { Link } from 'react-router-dom';
import { Lock, ShieldCheck, Cookie, Mail, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function Privacy() {
  const lastUpdated = 'February 2026';

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-12 pb-6">
        <div className="flex items-center gap-2 mb-3" style={{ color: '#5B7C99' }}>
          <Lock className="w-4 h-4" />
          <span className="text-xs uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, letterSpacing: '0.15em' }}>Privacy Policy</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
          What We Keep, What We Don't, and Why
        </h1>
        <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
          Your privacy matters. Here's exactly what data we collect, what we do with it, and how you can take it back — in plain English, no legalese buried in the fine print.
        </p>
        <p className="text-sm mt-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Last updated: {lastUpdated}</p>
      </div>

      {/* At-a-glance summary card */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="p-6 rounded-3xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #F0F5FA 0%, #FFFFFF 100%)', border: '1px solid #5B7C9930', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid="privacy-summary">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: '#5B7C9925' }}>
              <ShieldCheck className="w-5 h-5" style={{ color: '#3D5A75' }} />
            </div>
            <div style={{ fontFamily: 'Nunito, sans-serif', color: '#2C3E52', lineHeight: 1.7 }}>
              <h2 className="font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: 18 }}>The 30-second version</h2>
              <ul className="space-y-1">
                <li>• <strong>We collect only what we need</strong> to give you an account, remember your favorites, and send emails you asked for.</li>
                <li>• <strong>We never sell or rent your data</strong> — not to advertisers, marketers, or anyone else.</li>
                <li>• <strong>We use trusted partners</strong> (Stripe for payments, Resend for email) — nothing more.</li>
                <li>• <strong>You control your data</strong>. Unsubscribe, edit, or delete anytime.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#3E3B37', lineHeight: 1.8, fontSize: '16px' }}>

        <Section num="1" title="What we collect" tint="#5B7C99">
          <p><strong>When you create an account:</strong> your name, email address, and a password (stored hashed — we can't read it).</p>
          <p className="mt-2"><strong>When you use the site:</strong> which articles you save as favorites, which prayers you send love to, and anonymous view counts (so we know which pieces are helping people).</p>
          <p className="mt-2"><strong>When you book a consultation:</strong> the title and description of your situation, plus payment info (which goes directly to Stripe — we never see your card number).</p>
          <p className="mt-2"><strong>When you contact us or share on the Prayer Wall:</strong> whatever you write, plus the timestamp.</p>
          <p className="mt-2">We do <strong>not</strong> collect: your birthdate, address, phone number, precise location, browsing history outside our site, or anything from social media unless you paste it in.</p>
        </Section>

        <Section num="2" title="Why we collect it" tint="#78A18E">
          <ul className="space-y-1 pl-4">
            <li>• <strong>To run your account</strong> — log you in, remember your favorites, and let you unsubscribe.</li>
            <li>• <strong>To send emails you actually asked for</strong> — welcome email, article notifications, prayer digest.</li>
            <li>• <strong>To answer your consultation</strong> — Genesis reads what you share and writes you back.</li>
            <li>• <strong>To improve the site</strong> — anonymous view counts tell us which topics matter to readers.</li>
          </ul>
          <p className="mt-3">We <strong>do not</strong> use your data for behavioral advertising, algorithmic profiling, or training AI models.</p>
        </Section>

        <Section num="3" title="Who we share it with" tint="#E57A44">
          <p>The <strong>only</strong> third parties that see your data:</p>
          <ul className="mt-2 space-y-2 pl-4">
            <li>• <strong>Stripe</strong> (payments) — receives your card details directly from your browser. We never see the card number. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#5B7C99', fontWeight: 600 }}>Stripe's privacy policy</a>.</li>
            <li>• <strong>Resend</strong> (email delivery) — receives your email address and the email content when we send you a welcome, digest, or reminder. See <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#5B7C99', fontWeight: 600 }}>Resend's privacy policy</a>.</li>
            <li>• <strong>MongoDB Atlas / Emergent hosting</strong> — where our database and app live. See <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#5B7C99', fontWeight: 600 }}>MongoDB's privacy policy</a>.</li>
          </ul>
          <p className="mt-3"><strong>We do not sell your data to anyone. Ever.</strong></p>
        </Section>

        <Section num="4" title="Cookies and tracking" tint="#B77BB0" icon={Cookie}>
          <p>We use a small number of <strong>essential cookies</strong> to keep you logged in and remember your preferences. That's it.</p>
          <p className="mt-2">We do <strong>not</strong> use:</p>
          <ul className="mt-1 space-y-1 pl-4">
            <li>• Google Analytics or similar tracking pixels</li>
            <li>• Facebook Pixel, Twitter Pixel, or advertising cookies</li>
            <li>• Third-party session replay tools (like FullStory or Hotjar)</li>
          </ul>
        </Section>

        <Section num="5" title="Your rights and choices" tint="#E8C265">
          <p>You have the right to:</p>
          <ul className="mt-2 space-y-1 pl-4">
            <li>• <strong>See what we have on you</strong> — email us and we'll send you a copy.</li>
            <li>• <strong>Correct anything</strong> — update your name or email from your account.</li>
            <li>• <strong>Unsubscribe from emails</strong> — every email has a one-click unsubscribe link.</li>
            <li>• <strong>Delete your account</strong> — email us and we'll remove your account and personal data within 30 days.</li>
            <li>• <strong>Take it with you</strong> — request an export of your data in a portable format.</li>
          </ul>
          <p className="mt-3">If you live in California (CCPA), Virginia (VCDPA), or another jurisdiction with data-privacy laws, these rights apply to you too — we honor them regardless of where you live.</p>
        </Section>

        <Section num="6" title="How we protect your data" tint="#3D5A75">
          <p>Your password is stored <strong>hashed with bcrypt</strong>, meaning even we can't read it — we can only verify a login attempt matches. Traffic to and from the site uses <strong>HTTPS/TLS encryption</strong>. Card details never touch our servers (Stripe handles them directly).</p>
          <p className="mt-2">No system is perfect, though. If a security incident affects your data, we'll notify you promptly and honestly.</p>
        </Section>

        <Section num="7" title="Children" tint="#5B7C99">
          <p>Love Restored is meant for adults (18+). We do not knowingly collect information from children under 13. If you believe a child has created an account, please contact us and we'll remove it.</p>
        </Section>

        <Section num="8" title="Changes to this policy" tint="#78A18E">
          <p>If we ever change how we handle your data, we'll update this page and — for meaningful changes — email subscribed members. The "Last updated" date at the top will always reflect the most recent revision.</p>
        </Section>

        <Section num="9" title="Contact" tint="#E57A44" icon={Mail}>
          <p>Questions about your privacy, want your data exported, or want your account deleted?</p>
          <p className="mt-2"><Link to="/contact" style={{ color: '#E57A44', fontWeight: 700, textDecoration: 'underline' }}>Use our contact form</Link> and we'll respond within 5 business days.</p>
        </Section>

        <div className="p-6 rounded-3xl text-center mt-10" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <ShieldCheck className="w-6 h-6 mx-auto mb-3" style={{ color: '#5B7C99' }} />
          <p className="italic mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: 17 }}>
            "The Lord will keep you from all harm — he will watch over your life."
          </p>
          <p className="text-sm" style={{ color: '#8C867D', fontWeight: 600 }}>Psalm 121:7</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Section({ num, title, tint, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${tint}20`, color: tint, fontFamily: 'Merriweather, serif' }}>
          {Icon ? <Icon className="w-4 h-4" /> : num}
        </span>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h2>
      </div>
      <div className="pl-12">{children}</div>
    </div>
  );
}
